import type { jsPDF } from 'jspdf';
import { brand } from '@/config/brand';
import { ITEM_SEVERITY_LABEL, REPORT_TABS, itemsForTab, type ItemSeverity, type ReportView } from './report-view';

export interface PdfContact {
  name: string;
  phone: string;
}

type Rgb = readonly [number, number, number];

// The portal palette (@vnetwork/ui tokens), as the PDF cannot read CSS variables.
const INK: Rgb = [0x14, 0x17, 0x1c];
const MUTED: Rgb = [0x6b, 0x72, 0x80];
const LINE: Rgb = [0xdf, 0xe2, 0xe6];
const SURFACE: Rgb = [0xf6, 0xf7, 0xf8];
const BRAND: Rgb = [0xee, 0x24, 0x24];
const GOOD: Rgb = [0x12, 0x96, 0x50];
const WARN: Rgb = [0xcc, 0x8a, 0x00];
const BAD: Rgb = [0xc9, 0x2a, 0x37];

const SEVERITY_TONE: Record<ItemSeverity, { fill: Rgb; text: Rgb }> = {
  critical: { fill: [0xfd, 0xec, 0xed], text: [0x7e, 0x18, 0x22] },
  high: { fill: [0xfd, 0xec, 0xed], text: [0x7e, 0x18, 0x22] },
  medium: { fill: [0xff, 0xf8, 0xe6], text: [0x7a, 0x52, 0x00] },
  low: { fill: [0xe9, 0xf9, 0xef], text: [0x0a, 0x5c, 0x33] },
  info: { fill: [0xec, 0xee, 0xf0], text: MUTED },
};

const toneColor = (tone: 'good' | 'warn' | 'bad'): Rgb => (tone === 'good' ? GOOD : tone === 'warn' ? WARN : BAD);

const MARGIN = 40;
const PAGE_W = 595.28;
const PAGE_H = 841.89;
const CONTENT_W = PAGE_W - MARGIN * 2;
const FOOTER_Y = PAGE_H - 24;
const BOTTOM = PAGE_H - 48;

// The built-in Helvetica only has Latin-1: anything else would print as garbage.
const latin = (s: string) => s.replace(/[^\x20-\x7e\xa0-\xff\n]/g, '?');

interface PdfLogo {
  /** PNG data URL. */
  data: string;
  /** Width over height. */
  ratio: number;
}

const LOGO_PX_WIDTH = 720;

// The brand logo as a small PNG data URL (the source is 2504 px wide). A missing or broken logo
// must not block the download, so failures resolve to null and the report goes without it.
async function loadLogo(url: string): Promise<PdfLogo | null> {
  try {
    const blob = await (await fetch(url)).blob();
    const bitmap = await createImageBitmap(blob);
    const width = Math.min(LOGO_PX_WIDTH, bitmap.width);
    const height = Math.round((bitmap.height * width) / bitmap.width);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
    return { data: canvas.toDataURL('image/png'), ratio: width / height };
  } catch {
    return null;
  }
}

/** File name for a report: scan-report-<host>.pdf */
const reportFileName = (host: string) => `scan-report-${host.replace(/[^a-z0-9.-]+/gi, '_')}.pdf`;

/** Builds the report PDF and saves it as a download. jsPDF is loaded on demand. */
export async function downloadReportPdf(view: ReportView, host: string, scannedAt: number, contact: PdfContact) {
  const [{ jsPDF }, logo] = await Promise.all([import('jspdf'), loadLogo(brand.assets.logo)]);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  new ReportWriter(doc, view, host, scannedAt, contact, logo).write();
  doc.save(reportFileName(host));
}

class ReportWriter {
  private y = MARGIN;

  constructor(
    private readonly doc: jsPDF,
    private readonly view: ReportView,
    private readonly host: string,
    private readonly scannedAt: number,
    private readonly contact: PdfContact,
    private readonly logo: PdfLogo | null,
  ) {}

  write() {
    this.header();
    this.overview();
    this.findings();
    this.footers();
  }

  private color(kind: 'text' | 'fill' | 'draw', c: Rgb) {
    if (kind === 'text') this.doc.setTextColor(c[0], c[1], c[2]);
    else if (kind === 'fill') this.doc.setFillColor(c[0], c[1], c[2]);
    else this.doc.setDrawColor(c[0], c[1], c[2]);
  }

  private font(size: number, bold = false) {
    this.doc.setFont('helvetica', bold ? 'bold' : 'normal');
    this.doc.setFontSize(size);
  }

  private ensure(height: number) {
    if (this.y + height <= BOTTOM) return;
    this.doc.addPage();
    this.y = MARGIN;
  }

  private wrap(text: string, width: number): string[] {
    return this.doc.splitTextToSize(latin(text), width) as string[];
  }

  private header() {
    const { doc } = this;
    this.color('fill', BRAND);
    doc.rect(0, 0, PAGE_W, 6, 'F');

    // Brand logo on the left, product name on the right.
    const logoH = 18;
    if (this.logo) {
      doc.addImage(this.logo.data, 'PNG', MARGIN, this.y - 4, logoH * this.logo.ratio, logoH);
    } else {
      this.font(12, true);
      this.color('text', BRAND);
      doc.text(latin(brand.shortName), MARGIN, this.y + 10);
    }
    this.font(10);
    this.color('text', MUTED);
    doc.text('Security report', PAGE_W - MARGIN, this.y + 10, { align: 'right' });
    const nameX = PAGE_W - MARGIN - doc.getTextWidth('Security report') - 6;
    this.font(10, true);
    this.color('text', BRAND);
    doc.text('VScanner', nameX, this.y + 10, { align: 'right' });

    this.y += 50;
    this.font(22, true);
    this.color('text', INK);
    doc.text(this.wrap(this.host, CONTENT_W)[0] ?? '', MARGIN, this.y);

    this.y += 18;
    this.font(10);
    this.color('text', MUTED);
    doc.text(`Scanned ${new Date(this.scannedAt).toLocaleString()}`, MARGIN, this.y);

    this.y += 14;
    doc.text(latin(`Prepared for ${this.contact.name} · ${this.contact.phone}`), MARGIN, this.y);

    this.y += 16;
    this.color('draw', LINE);
    doc.line(MARGIN, this.y, PAGE_W - MARGIN, this.y);
    this.y += 22;
  }

  private overview() {
    const { doc } = this;
    const { result, jsCount } = this.view;
    const { headers, tls, owasp } = result.metrics;
    const tone = result.score >= 85 ? 'good' : result.score >= 65 ? 'warn' : 'bad';
    const gradeTone = (g: string) => (g === 'A' ? 'good' : g === 'B' ? 'warn' : 'bad');

    // Score block.
    const blockH = 84;
    this.color('fill', SURFACE);
    this.color('draw', LINE);
    doc.roundedRect(MARGIN, this.y, CONTENT_W, blockH, 8, 8, 'FD');
    this.font(38, true);
    this.color('text', toneColor(tone));
    doc.text(String(result.score), MARGIN + 24, this.y + 52);
    const scoreW = doc.getTextWidth(String(result.score));
    this.font(12);
    this.color('text', MUTED);
    doc.text('/ 100', MARGIN + 28 + scoreW, this.y + 52);

    this.font(10);
    const note = this.wrap(
      'Score reflects what is observable from outside the website. It is not a substitute for a penetration test.',
      CONTENT_W - 170,
    );
    doc.text(note, MARGIN + 150, this.y + 36);
    this.y += blockH + 14;

    // Four metric tiles.
    const gap = 10;
    const tileW = (CONTENT_W - gap * 3) / 4;
    const tileH = 78;
    const tiles: ReadonlyArray<{ name: string; value: string; note: string; tone: 'good' | 'warn' | 'bad' }> = [
      {
        name: 'Security headers',
        value: `${headers.present} / ${headers.total}`,
        note: `Missing ${headers.missing.join(', ') || 'none'}`,
        tone: gradeTone(headers.grade),
      },
      { name: 'SSL / TLS', value: tls.grade, note: `${tls.version}, valid ${tls.daysLeft} days`, tone: gradeTone(tls.grade) },
      {
        name: 'JavaScript',
        value: `${jsCount} obfuscated`,
        note: jsCount ? 'Obfuscated code detected' : 'No obfuscated code',
        tone: jsCount ? 'warn' : 'good',
      },
      {
        name: 'OWASP Top 10',
        value: `${owasp.flagged} categories`,
        note: owasp.flagged ? `${owasp.issues} findings` : 'No surface indicators',
        tone: owasp.flagged ? 'bad' : 'good',
      },
    ];
    tiles.forEach((t, i) => {
      const x = MARGIN + i * (tileW + gap);
      this.color('fill', [255, 255, 255]);
      this.color('draw', LINE);
      doc.roundedRect(x, this.y, tileW, tileH, 6, 6, 'FD');
      this.color('fill', toneColor(t.tone));
      doc.circle(x + tileW - 12, this.y + 14, 3.5, 'F');
      this.font(9);
      this.color('text', MUTED);
      doc.text(t.name, x + 10, this.y + 17);
      this.font(15, true);
      this.color('text', INK);
      doc.text(this.wrap(t.value, tileW - 20)[0] ?? '', x + 10, this.y + 42);
      this.font(8.5);
      this.color('text', MUTED);
      doc.text(this.wrap(t.note, tileW - 20).slice(0, 2), x + 10, this.y + 58);
    });
    this.y += tileH + 28;
  }

  private findings() {
    const { doc } = this;
    const { items } = this.view;

    this.ensure(40);
    this.font(14, true);
    this.color('text', INK);
    doc.text(`Issues (${items.length})`, MARGIN, this.y);
    this.y += 14;

    if (items.length === 0) {
      this.font(10);
      this.color('text', MUTED);
      doc.text('No issues were found.', MARGIN, this.y + 8);
      return;
    }

    // One section per category, in the order of the report tabs; empty ones are left out.
    REPORT_TABS.filter((t) => t.id !== 'all').forEach((section) => {
      const inSection = itemsForTab(items, section.id);
      if (inSection.length === 0) return;
      this.sectionHeading(section.list, inSection.length);
      inSection.forEach((item) => this.finding(item));
    });
  }

  private sectionHeading(title: string, count: number) {
    const { doc } = this;
    // Keep the heading together with the first finding below it.
    this.ensure(110);
    this.y += 10;
    this.color('fill', SURFACE);
    this.color('draw', LINE);
    doc.roundedRect(MARGIN, this.y, CONTENT_W, 26, 6, 6, 'FD');
    this.color('fill', BRAND);
    doc.rect(MARGIN, this.y + 6, 3, 14, 'F');
    this.font(11, true);
    this.color('text', INK);
    doc.text(title, MARGIN + 14, this.y + 17);
    this.font(10);
    this.color('text', MUTED);
    doc.text(`${count} ${count === 1 ? 'issue' : 'issues'}`, PAGE_W - MARGIN - 10, this.y + 17, { align: 'right' });
    this.y += 26;
  }

  private finding(item: ReportView['items'][number]) {
    const { doc } = this;
    const textX = MARGIN + 74;
    const textW = CONTENT_W - 74 - 70;

    const blocks: Array<{ lines: string[]; size: number; bold?: boolean; color: Rgb }> = [];
    blocks.push({ lines: this.wrap(item.title, textW), size: 11, bold: true, color: INK });
    if (item.detail) blocks.push({ lines: this.wrap(item.detail, textW), size: 9.5, color: MUTED });
    const meta: Array<[string, string]> = [
      ['Location', item.location],
      ['Parameter', item.parameter],
      ['Evidence', item.evidence],
    ];
    meta.filter(([, v]) => v).forEach(([k, v]) => blocks.push({ lines: this.wrap(`${k}: ${v}`, textW), size: 9, color: MUTED }));
    if (item.fix) {
      blocks.push({ lines: ['Remediation Guidance'], size: 9, bold: true, color: INK });
      blocks.push({ lines: this.wrap(item.fix, textW), size: 9.5, color: INK });
    }

    const lineH = (size: number) => size * 1.35;
    const height = blocks.reduce((h, b) => h + b.lines.length * lineH(b.size) + 3, 0) + 22;

    this.ensure(Math.min(height, 120));
    const top = this.y;

    // Severity badge and category.
    const tone = SEVERITY_TONE[item.severity];
    this.color('fill', tone.fill);
    doc.roundedRect(MARGIN, top + 12, 62, 18, 9, 9, 'F');
    this.font(8.5, true);
    this.color('text', tone.text);
    doc.text(ITEM_SEVERITY_LABEL[item.severity], MARGIN + 31, top + 24, { align: 'center' });
    this.font(8);
    this.color('text', MUTED);
    doc.text(latin(item.cat), PAGE_W - MARGIN, top + 24, { align: 'right' });

    // Body; flows onto the next page when long.
    let y = top + 22;
    blocks.forEach((b) => {
      this.font(b.size, b.bold);
      this.color('text', b.color);
      b.lines.forEach((line) => {
        if (y + lineH(b.size) > BOTTOM) {
          doc.addPage();
          y = MARGIN + 12;
          this.font(b.size, b.bold);
          this.color('text', b.color);
        }
        doc.text(line, textX, y);
        y += lineH(b.size);
      });
      y += 3;
    });

    this.y = y + 8;
    this.color('draw', LINE);
    doc.line(MARGIN, this.y, PAGE_W - MARGIN, this.y);
    this.y += 2;
  }

  private footers() {
    const { doc } = this;
    const pages = doc.getNumberOfPages();
    for (let p = 1; p <= pages; p += 1) {
      doc.setPage(p);
      this.color('draw', LINE);
      doc.line(MARGIN, FOOTER_Y - 12, PAGE_W - MARGIN, FOOTER_Y - 12);
      let textX = MARGIN;
      if (this.logo) {
        const h = 12;
        doc.addImage(this.logo.data, 'PNG', MARGIN, FOOTER_Y - 9, h * this.logo.ratio, h);
        textX += h * this.logo.ratio + 8;
      }
      this.font(8);
      this.color('text', MUTED);
      doc.text(latin(`${brand.legalName} · ${brand.hotline}`), textX, FOOTER_Y);
      doc.text(`Page ${p} of ${pages}`, PAGE_W - MARGIN, FOOTER_Y, { align: 'right' });
    }
  }
}
